<template>
  <div class="navbar-container">
    <el-menu mode="horizontal" background-color="transparent" text-color="#FFF" active-text-color="#409eff"
      class="modern-navbar" :ellipsis="false">
      <router-link :to="{ path: '/' }" class="logo-link">
        <el-menu-item index="0" class="logo-item">
          <img src="/static/media/Miandum.White.HOR.png" height="50" class="logo-img" />
        </el-menu-item>
      </router-link>

      <!-- Show modules directly if they fit, otherwise use dropdown -->
      <template v-if="!useModulesDropdown">
        <NavbarLink v-for="(m, index) in modules" :module="m.code" :text="m.name" :key="m.code"
          :index="(index + 999).toString()" />
      </template>
      <el-sub-menu v-else index="modules-dropdown" class="modules-dropdown">
        <template #title>
          <span>{{ $t('navbar.modules') || 'Modules' }}</span>
        </template>
        <NavbarLink v-for="(m, index) in modules" :module="m.code" :text="m.name" :key="m.code"
          :index="(index + 999).toString()" />
      </el-sub-menu>

      <el-menu-item v-if="currentCompany" index="company-display" class="company-display nav-spacer" disabled>
        <div class="company-badge">
          <span class="company-label">{{ $t('navbar.company') }}</span>
          <span class="company-name">{{ currentCompany.company_name }}</span>
        </div>
      </el-menu-item>

      <NavbarMenu index="50" :text="username" aclass="user-menu" />
    </el-menu>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { uiHelpers } from "../ui_helpers";
import NavbarLink from "./NavbarLink.vue";
import NavbarMenu from "./NavbarMenu.vue";

export default {
  components: {
    NavbarLink,
    NavbarMenu,
  },
  setup() {
    const store = useStore();
    const { getmoduleslist, modules } = uiHelpers("modules");
    getmoduleslist();

    const username = computed(
      () => store.state.auth.first_name + " " + store.state.auth.last_name
    );

    const currentCompany = computed(() => store.state.auth.current_company);

    // Determine if modules should be in a dropdown
    const useModulesDropdown = ref(false);

    const checkNavbarSpace = () => {
      const screenWidth = window.innerWidth;
      const moduleCount = modules.value ? modules.value.length : 0;

      // Calculate approximate space needed
      // Logo: ~200px, Company badge: ~250px, User menu: ~150px, Padding: ~100px
      const reservedSpace = 700;
      // Each module needs approximately 120px
      const spacePerModule = 120;
      const availableSpace = screenWidth - reservedSpace;
      const canFitModules = Math.floor(availableSpace / spacePerModule);

      // Use dropdown if modules don't fit or if more than 8 modules
      useModulesDropdown.value = moduleCount > canFitModules || moduleCount > 8;
    };

    onMounted(() => {
      checkNavbarSpace();
      window.addEventListener('resize', checkNavbarSpace);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkNavbarSpace);
    });

    return {
      username,
      modules,
      currentCompany,
      useModulesDropdown,
    };
  },
  async mounted() {
    await this.$store.dispatch('auth/fetchCurrentCompany');
    await this.$store.dispatch('auth/fetchCompanies')
  }
};
</script>
<style scoped>
.navbar-container {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  position: relative;
}

.navbar-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg,
      #409eff 0%,
      #67c23a 25%,
      #e6a23c 50%,
      #f56c6c 75%,
      #409eff 100%);
  opacity: 0.6;
}

.modern-navbar {
  border: none !important;
  padding: 0 20px;
  min-height: 60px;
}

.logo-link {
  text-decoration: none;
  margin-right: 10px;
}

.logo-item {
  padding: 5px 15px !important;
  background-color: transparent !important;
  border: none !important;
}

.logo-item:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.logo-img {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
  opacity: 0.9;
}

/* Push company display to the right */
.nav-spacer {
  margin-left: auto !important;
}

.company-display {
  cursor: default !important;
  opacity: 1 !important;
  background-color: transparent !important;
  padding: 0 15px !important;
}

.company-display:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.company-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  height: 32px;
}

.company-label {
  font-size: 11px;
  color: #a8b3c5;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.company-name {
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  padding: 12px 12px;
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.2) 0%, rgba(64, 158, 255, 0.1) 100%);
  border-radius: 4px;
  border: 1px solid rgba(64, 158, 255, 0.3);
  border-left: 3px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* Modern menu item styles */
:deep(.el-menu-item) {
  border: none !important;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  position: relative;
}

:deep(.el-menu-item:not(.company-display):hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

:deep(.el-menu-item.is-active) {
  background-color: rgba(64, 158, 255, 0.15) !important;
  border-bottom: 3px solid #409eff !important;
}

:deep(.el-sub-menu__title) {
  font-weight: 500;
  border: none !important;
  transition: all 0.3s ease;
}

:deep(.el-sub-menu__title:hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Dropdown/Popup menu styling */
:deep(.el-menu--popup) {
  background-color: #2c3e50 !important;
  border: 1px solid rgba(64, 158, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

:deep(.el-menu--popup .el-menu-item) {
  color: #ffffff !important;
  background-color: transparent !important;
}

:deep(.el-menu--popup .el-menu-item:hover) {
  background-color: rgba(64, 158, 255, 0.2) !important;
  color: #ffffff !important;
}

:deep(.el-menu--popup .el-menu-item.is-active) {
  background-color: rgba(64, 158, 255, 0.3) !important;
  color: #409eff !important;
}

:deep(.el-menu--popup .el-sub-menu__title) {
  color: #ffffff !important;
  background-color: transparent !important;
}

:deep(.el-menu--popup .el-sub-menu__title:hover) {
  background-color: rgba(64, 158, 255, 0.2) !important;
}

/* Responsive design */
@media (max-width: 1200px) {
  .modern-navbar {
    padding: 0 10px;
  }

  .company-label {
    display: none;
  }
}

@media (max-width: 768px) {
  .nav-links {
    gap: 0;
  }

  .company-badge {
    gap: 5px;
  }

  .company-name {
    font-size: 12px;
    padding: 4px 10px;
  }
}
</style>
